import { useEffect, useRef, useState } from "react";

import api from "../../../lib/api";

/* Date pickers fire on every change; wait for a pause before asking the server
 * to price them. */
const DEBOUNCE_MS = 350;

/**
 * Price a booking that does not exist yet.
 *
 * The server answers with both figures the desk needs — what is taken now and
 * what the stay is expected to cost between the guest's stated arrival and
 * departure — from the same module preChargeBooking uses, so the number shown
 * here is the number charged. Nothing about the quote is computed in the
 * browser; in particular the caller must not synthesise entry/exit events,
 * which is how this preview used to end up quoting a figure the charge never
 * matched.
 */
export default function useBookingQuote({
  installationId,
  rateCardId,
  startDay,
  endDay,
  expectedDepartureAt,
  expectedArrivalAt,
}) {
  const [state, setState] = useState({
    quote: null,
    details: null,
    loading: false,
    error: null,
  });

  /* Guards against a slow early request landing after a faster later one and
   * showing a price for dates the user has already changed. */
  const requestRef = useRef(0);

  const ready = Boolean(installationId && rateCardId && startDay && endDay);

  useEffect(() => {
    if (!ready) {
      setState({ quote: null, details: null, loading: false, error: null });
      return undefined;
    }

    const requestId = ++requestRef.current;
    setState((prev) => ({ ...prev, loading: true }));

    const timer = setTimeout(async () => {
      try {
        const { data } = await api.bookingDetails({
          installationId,
          rateCardId,
          booking: {
            startDay,
            endDay,
            expectedDepartureAt: expectedDepartureAt ?? null,
            expectedArrivalAt: expectedArrivalAt ?? null,
            /* Mirrors the real booking the charge will price moments later:
               with no entry event yet, an hourly stay starts from the booked
               startDay when that is later, and otherwise from createdAt. */
            createdAt: new Date().toISOString(),
          },
        });

        if (requestRef.current !== requestId) return;

        setState({
          quote: data?.quote ?? null,
          details: data?.details ?? null,
          loading: false,
          error: null,
        });
      } catch (e) {
        if (requestRef.current !== requestId) return;

        setState({
          quote: null,
          details: null,
          loading: false,
          error: e?.message || "Could not calculate the parking fee.",
        });
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    ready,
    installationId,
    rateCardId,
    startDay,
    endDay,
    expectedDepartureAt,
    expectedArrivalAt,
  ]);

  return state;
}
